"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { IconButton } from "./ui/icon-button"
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip"

interface Transform {
  x: number
  y: number
  scale: number
}

interface ZoomableViewerProps {
  svg: string
  ariaLabel?: string
  onClose: () => void
  // Optional overlay node rendered in the top-left of the viewer — used by
  // the chat surface to slot in a pin button so users can pin a design from
  // the fullscreen view as well as from the inline preview.
  pinOverlay?: React.ReactNode
  // When set, render a labeled "New project" CTA at top-center. Used by the
  // scrapbook viewer to seed a fresh chat session from the pinned design.
  onNewProject?: () => void
  newProjectLabel?: string
}

// Tracks history.back() calls we initiate ourselves so the resulting popstate
// events are not mistaken for user back-navigation. Module-level so it
// survives React Strict Mode's effect double-invoke and cross-instance cases.
let pendingOwnBacks = 0

const MIN_SCALE = 0.05
const MAX_SCALE = 80

function clampScale(s: number): number {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s))
}

function zoomAt(t: Transform, factor: number, focal: { x: number; y: number }): Transform {
  const next = clampScale(t.scale * factor)
  const k = next / t.scale
  return {
    scale: next,
    x: focal.x - (focal.x - t.x) * k,
    y: focal.y - (focal.y - t.y) * k,
  }
}

export default function ZoomableViewer({ svg, ariaLabel, onClose, pinOverlay, onNewProject, newProjectLabel = "Start new project" }: ZoomableViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, scale: 1 })
  const [ready, setReady] = useState(false)
  const [closing, setClosing] = useState(false)

  const handleClose = useCallback(() => setClosing(true), [])

  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
  const pinchRef = useRef<{ dist: number; cx: number; cy: number } | null>(null)

  useEffect(() => {
    function fit() {
      const container = containerRef.current
      const content = contentRef.current
      if (!container || !content) return
      const cw = container.clientWidth
      const ch = container.clientHeight
      const sw = content.offsetWidth
      const sh = content.offsetHeight
      if (!sw || !sh || !cw || !ch) return
      const scale = Math.min(cw / sw, ch / sh) * 0.92
      setTransform({
        scale,
        x: (cw - sw * scale) / 2,
        y: (ch - sh * scale) / 2,
      })
      setReady(true)
    }
    fit()
    window.addEventListener("resize", fit)
    return () => window.removeEventListener("resize", fit)
  }, [svg])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [handleClose])

  useEffect(() => {
    history.pushState({ loomViewer: true }, "")
    let popped = false
    const onPopState = () => {
      if (pendingOwnBacks > 0) { pendingOwnBacks--; return }
      popped = true
      handleClose()
    }
    window.addEventListener("popstate", onPopState)
    return () => {
      window.removeEventListener("popstate", onPopState)
      if (!popped) { pendingOwnBacks++; history.back() }
    }
  }, [handleClose])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const factor = Math.exp(-e.deltaY * 0.0015)
      setTransform((t) =>
        zoomAt(t, factor, { x: e.clientX - rect.left, y: e.clientY - rect.top }),
      )
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 2) {
      const [a, b] = Array.from(pointersRef.current.values())
      pinchRef.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
      }
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointersRef.current.get(e.pointerId)
    if (!prev) return
    const next = { x: e.clientX, y: e.clientY }
    pointersRef.current.set(e.pointerId, next)

    if (pointersRef.current.size === 1) {
      const dx = next.x - prev.x
      const dy = next.y - prev.y
      setTransform((t) => ({ ...t, x: t.x + dx, y: t.y + dy }))
      return
    }

    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = Array.from(pointersRef.current.values())
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      const cx = (a.x + b.x) / 2
      const cy = (a.y + b.y) / 2
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const focal = { x: cx - rect.left, y: cy - rect.top }
      const factor = pinchRef.current.dist > 0 ? dist / pinchRef.current.dist : 1
      const tx = cx - pinchRef.current.cx
      const ty = cy - pinchRef.current.cy
      setTransform((t) => {
        const z = zoomAt(t, factor, focal)
        return { ...z, x: z.x + tx, y: z.y + ty }
      })
      pinchRef.current = { dist, cx, cy }
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
  }

  return (
    <div
      className={`fixed inset-0 z-50 bg-background ${closing ? "animate-zoom-out" : "animate-zoom-in"}`}
      role="dialog"
      aria-label={ariaLabel ?? "Zoomable view"}
      aria-modal="true"
      onAnimationEnd={closing ? onClose : undefined}
    >
      <div
        ref={containerRef}
        className="absolute inset-0 touch-none overflow-hidden cursor-grab active:cursor-grabbing select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          ref={contentRef}
          className="absolute top-0 left-0 origin-top-left will-change-transform [&>svg]:block"
          style={{
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
            opacity: ready ? 1 : 0,
            transition: ready ? "none" : "opacity 0.15s ease-out",
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
      {/* Pin overlay slotted top-left, opposite the action cluster. */}
      {pinOverlay && (
        <div className="fixed top-4 left-4 z-10">
          {pinOverlay}
        </div>
      )}
      {/* Top-right action cluster — "New project" CTA (when launched from
          scrapbook) sits to the left of the close button so the close
          stays in its conventional corner. */}
      <div className="fixed top-4 right-4 z-10 flex items-center gap-2">
        {onNewProject && (
          <button
            type="button"
            onClick={onNewProject}
            className="px-4 h-11 rounded-full bg-foreground text-background text-base font-medium shadow-lg hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
          >
            {newProjectLabel}
          </button>
        )}
        <Tooltip>
          <TooltipTrigger
            render={
              <IconButton
                onClick={handleClose}
                aria-label="Close"
                className="bg-foreground text-background shadow-lg hover:opacity-90 transition-opacity"
              />
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </TooltipTrigger>
          <TooltipContent side="left">Close</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
